import React, { useState, useEffect, Fragment } from 'react';
import Select from 'react-select';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import Modal from 'react-modal';
import styled from 'styled-components';
import './bookingSection.scss';
import moment from 'moment';
import { Spinner } from 'react-bootstrap';

Modal.setAppElement('#root');

const DatePickerButton = styled.div`
  background: #f5f6f7;
  border: none;
  width: 100%;
  height: 52px;
  padding: 0 5px;
  text-overflow: ellipsis;
  cursor: pointer;
  text-align: center;
  line-height: 52px;
`;

const BookingSection = ({
  place,
  guests,
  nights,
  handleMessageChange,
  handleApply,
  handleGuestChange,
  customSelect,
  scrollToRef,
  handleBookingRequest,
  bookingFormErrors,
  validateErrors,
  modalStyles,
}) => {
  // Parse initial dates from query parameters or use defaults
  const queryParams = new URLSearchParams(window.location.search);
  const initialStartDate = queryParams.get('startDate')
    ? moment(queryParams.get('startDate'), 'DD-MM-YYYY')
    : moment();
  const initialEndDate = queryParams.get('endDate')
    ? moment(queryParams.get('endDate'), 'DD-MM-YYYY')
    : moment().add(1, 'day');

  const [startDateSelected, setStartDateSelected] = useState(initialStartDate);
  const [endDateSelected, setEndDateSelected] = useState(initialEndDate);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  // Fetch unavailable dates when place ID changes
  useEffect(() => {
    if (place?.id) {
      fetchCalendarData();
    }
  }, [place?.id]);

  // Initialize selected dates when component mounts
  useEffect(() => {
    const picker = { startDate: initialStartDate, endDate: initialEndDate };
    handleApply(null, picker); // Initialize parent state
  }, []); // Runs only once on mount

  // Update dates in parent state whenever selected dates change
  useEffect(() => {
    const picker = { startDate: startDateSelected, endDate: endDateSelected };
    handleApply(null, picker);
  }, [startDateSelected, endDateSelected, handleApply]);

  // Fetch calendar data for unavailable dates
  const fetchCalendarData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/calendar/${place.id}/approved`);
      const { availability, bookings } = response.data;

      const formatDates = (items, startKey, endKey) =>
        items.flatMap((item) => {
          const dates = [];
          let currentDate = moment(item[startKey]);
          const endDate = moment(item[endKey]);

          while (currentDate.isSameOrBefore(endDate)) {
            dates.push(currentDate.format('YYYY-MM-DD'));
            currentDate.add(1, 'day');
          }

          return dates;
        });

      const formattedAvailabilityDates = availability
        ? formatDates(availability, 'startDate', 'endDate')
        : [];
      const formattedBookingDates = bookings
        ? formatDates(bookings, 'start_date', 'end_date')
        : [];

      setUnavailableDates([...new Set([...formattedAvailabilityDates, ...formattedBookingDates])]);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeApply = (e, picker) => {
    setStartDateSelected(picker.startDate);
    setEndDateSelected(picker.endDate);
  };

  const openModal = () => setModalIsOpen(true);
  const closeModal = () => setModalIsOpen(false);

  if (!place?.id || loading) {
    return (
      <div style={{ textAlign: 'center' }}>
        <Spinner animation="border" role="status" className="me-2" />
        <p>Loading calendar data...</p>
      </div>
    );
  }

  return (
    <Fragment>
      <div id="bookingSection" className="sticky-bar" ref={scrollToRef}>
        <div className="card card-thinking card-outline-grey border-0 mb-3" style={{ width: '100%' }}>
          <div className="card-body contact_form">
            <h5 className="text-black font-all-round font-weight-600 font-size-20">Booking request</h5>
            <form
              method="POST"
              action="/bookings"
              name="BookingRequestForm"
              aria-label="RequestBooking"
              onSubmit={(e) => {
                e.preventDefault();
                handleBookingRequest(e);
              }}
            >
              <div className="form-group">
                <label className="text-black font-weight-600">Check-in / Check-out</label>
                <DateRangePicker
                  initialSettings={{
                    startDate: startDateSelected,
                    endDate: endDateSelected,
                    minDate: moment().toDate(),
                    isInvalidDate: (momentDate) =>
                      unavailableDates.includes(momentDate.format('YYYY-MM-DD')),
                  }}
                  onApply={handleDateRangeApply}
                >
                  <DatePickerButton>
                    {`${startDateSelected.format('DD-MM-YYYY')} - ${endDateSelected.format('DD-MM-YYYY')}`}
                  </DatePickerButton>
                </DateRangePicker>
                {bookingFormErrors.dates && <div className="booking_form_error">{bookingFormErrors.dates}</div>}
              </div>
              <div className="form-group">
                <label className="text-black font-open-sans font-weight-600 font-size-14">
                  Number of guests
                </label>
                <Select
                  id="guests_number"
                  name="guests_number"
                  styles={customSelect}
                  isDisabled={false}
                  isLoading={false}
                  isClearable={false}
                  isRtl={false}
                  isSearchable={true}
                  options={guests}
                  required
                  onChange={handleGuestChange}
                  className={
                    bookingFormErrors.guests ? 'border border-danger' : null
                  }
                />
                {bookingFormErrors.guests && (
                  <div className="booking_form_error">
                    {bookingFormErrors.guests}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="text-black font-open-sans font-weight-600 font-size-14">
                  Message
                </label>
                <textarea
                  name=""
                  id=""
                  cols="30"
                  rows="5"
                  placeholder={`Tell ${place.user.forename} why you'd like to stay with them...`}
                  className="form-control"
                  onChange={handleMessageChange}
                />
              </div>
              <div className="d-flex justify-content-between pb-2">
                <span
                  className="font-open-sans font-size-16 font-weight-400 text-black"
                  dangerouslySetInnerHTML={{
                    __html: `${place.currency}${place.price} x ${nights == 1 ? '1 night' : `${nights} nights`}`,
                  }}
                ></span>
                <span
                  className="font-open-sans font-size-16 font-weight-400 text-black"
                  dangerouslySetInnerHTML={{
                    __html: `${place.currency}${nights * place.price}`,
                  }}
                />
              </div>
              <div className="d-flex justify-content-between pb-2">
                <span className="font-open-sans font-size-16 font-weight-400 text-black">
                  Freebird booking fee ({place.feePercentage}%)
                </span>
                <span
                  className="font-open-sans font-size-16 font-weight-400 text-black"
                  dangerouslySetInnerHTML={{
                    __html: `${place.currency}${(place.fee * nights).toFixed(2)}`,
                  }}
                />
              </div>
              <hr />
              <div className="d-flex justify-content-between pb-2">
                <span className="font-open-sans font-size-22 font-weight-700 text-black">
                  Total
                </span>
                <span
                  className="font-open-sans font-size-22 font-weight-700 text-black"
                  dangerouslySetInnerHTML={{
                    __html: `${place.currency}${(nights * place.price + +place.fee * nights).toFixed(2)}`,
                  }}
                />
              </div>
              <div className="d-flex pb-2 d-flex justify-content-center">
                <span className="font-open-sans font-size-14 font-weight-400 text-black py-2">
                  To reserve this property you must pay only the{' '}
                  {place.feePercentage}% booking fee now to Freebird. The host
                  will confirm with you when you should pay the balance directly
                  to them.
                </span>
              </div>
              <div className="submit-request-btn">
                <div
                  className="btn btn-orange w-100"
                  onClick={(e) => {
                    if (validateErrors()) {
                      openModal(e)
                    }
                  }}
                >
                  Request Booking
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={modalStyles}
        contentLabel="Confirmation"
      >
        <div className="bookingSection__modal-wrapper">
          <h5>
            You are about to send a booking request to {place.user.forename}.
            <br /> Do you want to proceed?
          </h5>
          <div className="bookingSection__modal-wrapper__button-wrapper">
            <button className="btn btn-gray" onClick={closeModal}>
              Cancel
            </button>
            <button
              className="btn btn-green"
              onClick={(e) => {
                handleBookingRequest(e);
                closeModal();
              }}
            >
              Send
            </button>
          </div>
        </div>
      </Modal>
    </Fragment>
  );
};

export default BookingSection;
